/**
 * The upload queue: what happens after a drop.
 *
 * What this replaces was a `for` loop awaiting one `uploadFile` at a time, with
 * progress keyed by filename and no way to stop. That was fine for the button —
 * you pick three files — and is not fine for a dropped folder, where the honest
 * requirements are:
 *
 *  · **an id per entry, not a filename.** Two files called `logo.png` in two
 *    dropped folders collided in the old progress list: one row, two uploads,
 *    and the bar jumping between them.
 *  · **cancel.** A 4 GB file started by accident had to be waited out.
 *  · **bounded concurrency.** Serial wastes a connection that could carry three
 *    files; unbounded opens two hundred and finishes none of them, and the
 *    server's own limit is 300 upload starts an hour.
 *  · **retry.** There were **no retries anywhere** — not per chunk, not per
 *    file. One dropped packet on chunk 900 of 1000 lost the whole upload.
 *
 * ── Deliberately not in here ────────────────────────────────────────────────
 * Encryption, keys, node creation, the wire. This module decides *what runs
 * when*; `nodes.ts::uploadFile` does the work. That split is what lets this be a
 * pure state machine with unit tests, in a repo with no jsdom and no Worker test
 * harness — the same reason `crypto/stream.ts` keeps `planUpload` separate from
 * the streaming.
 */

export type QueueStatus = 'waiting' | 'running' | 'done' | 'failed' | 'cancelled';

export interface QueueItem {
  /** Stable and unique. Never the filename — see the note above. */
  id: string;
  name: string;
  /** Folder path relative to the upload root, from the drop. */
  path: string[];
  size: number;
  status: QueueStatus;
  bytesDone: number;
  chunk: number;
  chunks: number;
  error?: string;
}

/** What the runner needs of a file, so tests need not build a real one. */
export interface QueueEntry {
  name: string;
  size: number;
  path: string[];
  /** Opaque to this module; handed straight back to `run`. */
  payload: unknown;
}

export interface RunContext {
  signal: AbortSignal;
  onProgress: (p: { bytesDone: number; chunk: number; chunks: number }) => void;
}

export interface QueueOptions {
  /**
   * How many uploads run at once.
   *
   * Three: enough to keep a connection busy while one file is between chunks,
   * few enough to stay clear of the server's 300-starts-an-hour limit during a
   * large drop, and few enough that the progress list is readable.
   */
  concurrency?: number;
  /** Attempts per file, including the first. */
  attempts?: number;
  /** Backoff before attempt n (1-based). Injected so tests need no timers. */
  backoffMs?: (attempt: number) => number;
  /** Sleep, injected for the same reason. */
  sleep?: (ms: number) => Promise<void>;
}

export const DEFAULT_CONCURRENCY = 3;
export const DEFAULT_ATTEMPTS = 3;

/** 400ms, 1.6s. Short: a stalled upload is more likely a blip than an outage,
 *  and somebody is watching a progress bar. */
export const defaultBackoff = (attempt: number): number => 400 * 4 ** (attempt - 1);

let counter = 0;
/** Monotonic within a session, which is all "unique per queue entry" needs. */
export function nextId(): string {
  counter += 1;
  return `u${counter}`;
}

/**
 * A running set of uploads.
 *
 * One instance per drop. `items()` is a snapshot for rendering; `onChange` fires
 * whenever any of it moves.
 */
export class UploadQueue {
  private readonly opts: Required<QueueOptions>;
  private readonly entries = new Map<string, QueueEntry>();
  private readonly controllers = new Map<string, AbortController>();
  private state: QueueItem[] = [];
  private listeners = new Set<() => void>();

  constructor(options: QueueOptions = {}) {
    this.opts = {
      concurrency: options.concurrency ?? DEFAULT_CONCURRENCY,
      attempts: options.attempts ?? DEFAULT_ATTEMPTS,
      backoffMs: options.backoffMs ?? defaultBackoff,
      sleep: options.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)))
    };
  }

  add(entries: QueueEntry[]): QueueItem[] {
    const added = entries.map((entry) => {
      const id = nextId();
      this.entries.set(id, entry);
      const item: QueueItem = {
        id,
        name: entry.name,
        path: entry.path,
        size: entry.size,
        status: 'waiting',
        bytesDone: 0,
        chunk: 0,
        chunks: 0
      };
      this.state.push(item);
      return item;
    });
    this.emit();
    return added;
  }

  items(): QueueItem[] {
    return this.state.map((i) => ({ ...i }));
  }

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /**
   * Stop one upload.
   *
   * A cancelled item is `cancelled`, not `failed` — the difference matters to
   * whoever is reading the list, because one of those is their doing.
   */
  cancel(id: string): void {
    this.controllers.get(id)?.abort();
    const item = this.state.find((i) => i.id === id);
    // A waiting item has no controller to abort, so it is marked here and
    // skipped when its turn comes.
    if (item && (item.status === 'waiting' || item.status === 'running')) {
      item.status = 'cancelled';
      this.emit();
    }
  }

  cancelAll(): void {
    for (const item of this.state) this.cancel(item.id);
  }

  /**
   * Run everything queued.
   *
   * `run` uploads one entry and resolves when it is stored. Rejecting means the
   * attempt failed; throwing an `AbortError` (or any error while the signal is
   * aborted) means it was cancelled and must not be retried.
   */
  async start(run: (entry: QueueEntry, ctx: RunContext) => Promise<void>): Promise<void> {
    const pending = this.state.filter((i) => i.status === 'waiting').map((i) => i.id);
    let cursor = 0;

    const worker = async (): Promise<void> => {
      for (;;) {
        const id = pending[cursor++];
        if (id === undefined) return;
        await this.runOne(id, run);
      }
    };

    const workers = Array.from({ length: Math.min(this.opts.concurrency, pending.length) },
      () => worker());
    await Promise.all(workers);
  }

  private async runOne(
    id: string,
    run: (entry: QueueEntry, ctx: RunContext) => Promise<void>
  ): Promise<void> {
    const item = this.state.find((i) => i.id === id);
    const entry = this.entries.get(id);
    if (!item || !entry) return;
    // Cancelled while it sat in the queue.
    if (item.status === 'cancelled') return;

    const controller = new AbortController();
    this.controllers.set(id, controller);

    item.status = 'running';
    this.emit();

    for (let attempt = 1; attempt <= this.opts.attempts; attempt++) {
      try {
        await run(entry, {
          signal: controller.signal,
          onProgress: (p) => {
            item.bytesDone = p.bytesDone;
            item.chunk = p.chunk;
            item.chunks = p.chunks;
            this.emit();
          }
        });
        item.status = 'done';
        item.bytesDone = item.size;
        this.controllers.delete(id);
        this.emit();
        return;
      } catch (err) {
        if (controller.signal.aborted) {
          item.status = 'cancelled';
          this.controllers.delete(id);
          this.emit();
          return;
        }
        const last = attempt === this.opts.attempts;
        if (last) {
          item.status = 'failed';
          item.error = err instanceof Error ? err.message : String(err);
          this.controllers.delete(id);
          this.emit();
          return;
        }
        // Progress resets: a retry re-uploads from the first chunk, because
        // `uploads.start` mints a new revision id and every chunk's AAD is bound
        // to it. Resuming a half-finished revision is a real feature and a
        // different one.
        item.bytesDone = 0;
        item.chunk = 0;
        this.emit();
        await this.opts.sleep(this.opts.backoffMs(attempt));
      }
    }
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }
}

/** Totals for the progress panel. */
export function summarise(items: QueueItem[]): {
  total: number;
  done: number;
  failed: number;
  active: number;
  bytesDone: number;
  bytesTotal: number;
} {
  return {
    total: items.length,
    done: items.filter((i) => i.status === 'done').length,
    failed: items.filter((i) => i.status === 'failed').length,
    active: items.filter((i) => i.status === 'running').length,
    // Cancelled and failed bytes are excluded: a bar that counts bytes nobody
    // stored would sit at 100% over a queue that half worked.
    bytesDone: items.reduce((sum, i) => sum + (i.status === 'done' ? i.size : i.bytesDone), 0),
    bytesTotal: items
      .filter((i) => i.status !== 'cancelled')
      .reduce((sum, i) => sum + i.size, 0)
  };
}
